import React, { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Micro } from "./ui";

/**
 * The "On this page" rail. A copy of the live rail markup and observer
 * (VID/Header.tsx:164-196, :430-461): Book active pill (D2), no spy pause,
 * `aria-current="true"`, the label hidden below xl, chips scroll sideways
 * rather than wrap.
 *
 * Product-local, per the plan's "Rail" row:
 *   - the label is --text-2, not the live --muted (contrast);
 *   - the targets carry SECTION_SCROLL_MARGIN (`scroll-mt-[76px]`: 60px
 *     sticky TopBar + 16px), so a click leaves the section's top clear of it;
 *   - pills are 40px below md (handoff control sizes);
 *   - the pick is made over every target's last known state, not over the
 *     callback's `entries`. A callback lists only the targets whose state
 *     CHANGED, and the live rail picks the topmost of those: with two short
 *     strips in the band at once (Consumer Voice and Product pages are often
 *     ~100px tall; the band is ~310px at 900px high), scrolling the first out
 *     reports only that one, so the second was never picked and the chip
 *     stayed put. The video page's tall panels rarely hit it;
 *   - a click makes its chip active at once and holds it until the reader
 *     scrolls by hand (wheel, touch, a key, a press on the page). The band
 *     alone can't do it when the page can't scroll a later section up into
 *     the band (short strips above, a short or empty Videos section): the
 *     click scrolls to the bottom, the band still holds Consumer Voice, and
 *     the chip never moved. This departs from the plan's "no pause", which
 *     copied the live rail; the live rail isn't changed;
 *   - at the bottom of the page (scrolled, and nothing left to scroll) the
 *     last section whose top is on screen is active, the usual scroll-spy
 *     end rule, for the same short-page case scrolled by hand; at the very
 *     top, the first (the seed's rule). The band and both ends are re-read on
 *     every scroll frame, since at either end nothing may cross the band's
 *     edges for the observer to report.
 *
 * Data-driven: the page passes one chip per section, in DOM order
 * (SECTION_ORDER: Product pages → Consumer Voice → AI Presence → Videos).
 *
 * Each id is resolved once per bind: every section keeps one root element for
 * the page's life (Consumer Voice and Product pages render one SectionCard in
 * every state, Videos one <section>; their dialogs are portals).
 */

export const SECTION_IDS = {
  consumerVoice: "product-consumer-voice",
  aiPresence: "product-ai-presence",
  pages: "product-pages",
  videos: "product-videos",
} as const;

/** The sections in DOM order (D1) — the rail's chips and the skeleton's stand-ins. */
export const SECTION_ORDER = [SECTION_IDS.pages, SECTION_IDS.consumerVoice, SECTION_IDS.aiPresence, SECTION_IDS.videos];

/**
 * Every section root's scroll margin: the 60px sticky TopBar + 16px, so a
 * rail click (or the page's scroll rule) leaves the section's top clear of it.
 * The full class literal, so Tailwind's scanner sees it.
 */
export const SECTION_SCROLL_MARGIN = "scroll-mt-[76px]";

/** The input that means the reader is scrolling by hand (a wheel, a touch, a key, a press). */
export const HAND_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"] as const;

export interface RailChip {
  /** The section element's id — one of SECTION_IDS. */
  id: string;
  label: string;
  /** Shown after the label when set ("Product pages" at 2 or more). */
  count?: number;
}

interface Props {
  chips: RailChip[];
  /**
   * Re-binds the observer when it changes, so the rail re-picks after the
   * sections reload (the page bumps it when the settings modal closes): a new
   * IntersectionObserver reports every target once, so the chip catches up
   * without a scroll. It matters after a chip click whose pin a press released
   * (opening the modal from the ⋮).
   */
  refreshKey?: string | number;
  className?: string;
}

export const SectionRail: FC<Props> = ({ chips, refreshKey, className }) => {
  const ids = chips.map((chip) => chip.id).join("|");

  // The active chip follows the scroll position. The margins narrow the
  // observation band to the top of the viewport, so "active" means the section
  // you are reading rather than any section merely on screen. The seed is the
  // FIRST section in the DOM — the observer only fires once something
  // intersects, so at scroll top the rail would otherwise highlight nothing.
  const [activeChip, setActiveChip] = useState(chips[0]?.id ?? "");
  // The chip a click chose, held until the reader scrolls by hand.
  const pinned = useRef<string | null>(null);

  // Hand scrolling releases a click's pin. The programmatic smooth scroll a
  // click starts fires `scroll` only, never these. A press on a chip
  // releases too, and its click pins again right after.
  useEffect(() => {
    const release = () => {
      pinned.current = null;
    };
    HAND_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, release, { capture: true, passive: true }));
    return () => HAND_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, release, { capture: true }));
  }, []);

  useEffect(() => {
    const list = ids ? ids.split("|") : [];
    // A chip that went away can't stay active (or pinned).
    setActiveChip((current) => (list.includes(current) ? current : list[0] ?? ""));
    if (pinned.current && !list.includes(pinned.current)) pinned.current = null;
    if (list.length === 0 || typeof IntersectionObserver === "undefined") return undefined;

    // What each id resolves to (null = not in the DOM).
    const bound = list.map((id) => document.getElementById(id));
    // Each target's last reported state: in the band or not.
    const inBand = new Map<Element, boolean>();

    const pick = () => {
      if (pinned.current) return;
      // Back at the very top, the first section: the seed's own rule. A fast
      // hand scroll can jump past every band crossing on its way up.
      if (window.scrollY <= 0) {
        const first = bound.find((node) => node && node.getClientRects().length > 0);
        if (first) {
          setActiveChip(first.id);
          return;
        }
      }
      const doc = document.scrollingElement || document.documentElement;
      const atEnd = window.scrollY > 0 && window.innerHeight + window.scrollY >= doc.scrollHeight - 1;
      if (atEnd) {
        const last = [...bound]
          .reverse()
          .find((node) => node && node.getClientRects().length > 0 && node.getBoundingClientRect().top < window.innerHeight);
        if (last) {
          setActiveChip(last.id);
          return;
        }
      }
      // The topmost section in the band is the first in DOM order: the
      // sections stack in chip order. None in the band keeps the chip.
      const topMost = bound.find((node) => node && inBand.get(node));
      if (topMost) setActiveChip(topMost.id);
    };

    // A new observer reports every target once on binding, so the active chip
    // catches up without waiting for a scroll (refreshKey).
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => inBand.set(entry.target, entry.isIntersecting));
        pick();
      },
      { rootMargin: "-96px 0px -55% 0px", threshold: 0 },
    );
    bound.forEach((node) => node && observer.observe(node));

    // One pick per frame while scrolling.
    let frame: number | null = null;
    const onScroll = () => {
      if (frame != null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        pick();
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      if (frame != null) window.cancelAnimationFrame(frame);
    };
  }, [ids, refreshKey]);

  const goToSection = (id: string) => {
    pinned.current = id;
    setActiveChip(id);
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    document.getElementById(id)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
  };

  if (chips.length === 0) return null;

  return (
    // A full-width row under the header. It never wraps to a second line —
    // the label drops first (below xl), then the chips scroll.
    <nav
      aria-label="On this page"
      className={classNames("flex items-center gap-2 flex-nowrap overflow-x-auto -mx-1 px-1", className)}
      style={{ scrollbarWidth: "none" }}
    >
      <Micro className="mr-0.5 shrink-0 hidden xl:inline">On this page</Micro>
      {chips.map((chip) => {
        const isActive = activeChip === chip.id;
        return (
          <button
            key={chip.id}
            type="button"
            onClick={() => goToSection(chip.id)}
            aria-current={isActive ? "true" : undefined}
            className={`inline-flex items-center gap-[7px] h-7 max-md:h-10 px-3 rounded-full border shrink-0 transition-colors ${
              isActive
                ? "bg-text-1 border-transparent text-surface"
                : "bg-surface border-border-token text-text-1 hover:border-accent"
            }`}
            style={{ fontSize: 12 }}
          >
            {chip.label}
            {chip.count != null && (
              <span className={isActive ? "opacity-70 tabular-nums" : "text-muted tabular-nums"}>{chip.count}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
};
